import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { tasksApi } from '@/lib/api/tasks'
import type { TaskPalletRow } from '@/types'

interface WorkerPalletsWSMessage {
  type: string
  pallets: TaskPalletRow[]
}

export function useWorkerPalletWebSocket(
  taskUuid: string | undefined,
  workerName: string | undefined,
  activePalletNum: number | null,
) {
  const qc = useQueryClient()
  const wsRef = useRef<WebSocket | null>(null)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closedRef = useRef(false)
  const activeRef = useRef<number | null>(activePalletNum)

  useEffect(() => {
    activeRef.current = activePalletNum
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({
      type: 'active_pallet',
      active_pallet_num: activePalletNum && activePalletNum > 0 ? activePalletNum : null,
    }))
  }, [activePalletNum])

  useEffect(() => {
    if (!taskUuid || !workerName) return

    closedRef.current = false

    const connect = () => {
      const ws = new WebSocket(tasksApi.workerPalletsWebSocketUrl(taskUuid, workerName))
      wsRef.current = ws

      ws.onopen = () => {
        ws.send(JSON.stringify({
          type: 'active_pallet',
          active_pallet_num: activeRef.current && activeRef.current > 0 ? activeRef.current : null,
        }))
      }
      ws.onclose = () => {
        if (wsRef.current === ws) {
          wsRef.current = null
        }
        if (!closedRef.current) {
          retryTimerRef.current = setTimeout(connect, 500)
        }
      }
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(String(ev.data)) as WorkerPalletsWSMessage
          if (msg.type !== 'pallets' || !Array.isArray(msg.pallets)) return
          qc.setQueryData<TaskPalletRow[]>(['task-pallets', taskUuid], msg.pallets)
        } catch {
          // ignore malformed frames
        }
      }
    }

    connect()
    return () => {
      closedRef.current = true
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
      wsRef.current?.close()
      wsRef.current = null
    }
  }, [taskUuid, workerName, qc])
}
